import { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import useAuth from '../Context/Hooks/useCustomAuth';
import { hasPermission } from '../Utils/permissions';

// ─── Role hierarchy ───────────────────────────────────────────────────────────
// GATEWAY_ADMIN (3) > CLIENT_ADMIN (2) > USER (1)
// allowedRoles: explicit list of roles that can access the route/component
// minRole: minimum role level required (everyone at that level or above)

const ROLE_LEVEL = { GATEWAY_ADMIN: 3, CLIENT_ADMIN: 2, USER: 1 };

const AuthGuard = ({ children, roles, allowedRoles, minRole, requiredPermission, componentPermission, moduleID }) => {
  const auth = useAuth();
  const location = useLocation();
  const [requestedLocation, setRequestedLocation] = useState(null);

  const handleDeny = () =>
    (componentPermission ? <></> : <Navigate
to="/error/401"
replace
    />);

  // Not authenticated — shouldn't reach here since Routes handles this,
  // but kept as safety net
  if (!auth.isAuthenticated || !auth.user) {
    return handleDeny();
  }

  const userRole = auth.user.role ?? 'USER';
  const userPermissions = auth.user.permissions ?? [];

  // ── New system: allowedRoles (explicit list) ────────────────────────────────
  if (allowedRoles?.length > 0) {
    if (!allowedRoles.includes(userRole)) {
      return handleDeny();
    }
  }

  // ── New system: requiredPermission ────────────────────────────────────────
  if (requiredPermission) {
    if (!hasPermission(userPermissions, requiredPermission)) {
      return handleDeny();
    }
  }

  // ── New system: minRole (hierarchy-based) ──────────────────────────────────
  if (minRole) {
    if ((ROLE_LEVEL[userRole] ?? 0) < (ROLE_LEVEL[minRole] ?? 0)) {
      return handleDeny();
    }
  }

  // ── Legacy system: roles string + userModules (kept for existing guards) ───
  if (process.env.REACT_APP_CUSTOM_AUTH !== 'NONE' && roles) {
    const hasRole = auth.user.roles?.includes(roles);
    const hasModule = !moduleID || !!auth.user.userModules?.find((x) => x.id === moduleID)?.enable;
    if (!hasRole || !hasModule) {
      return handleDeny();
    }
  }

  if (requestedLocation && location.pathname !== requestedLocation) {
    setRequestedLocation(null);
    return <Navigate to={requestedLocation} />;
  }

  return <>{children}</>;
};

AuthGuard.propTypes = {
  children: PropTypes.node,
  // Legacy
  roles: PropTypes.string,
  moduleID: PropTypes.string,
  componentPermission: PropTypes.bool,
  // New
  allowedRoles: PropTypes.arrayOf(PropTypes.oneOf(['GATEWAY_ADMIN', 'CLIENT_ADMIN', 'USER'])),
  minRole: PropTypes.oneOf(['GATEWAY_ADMIN', 'CLIENT_ADMIN', 'USER']),
  requiredPermission: PropTypes.string,
};

AuthGuard.defaultProps = {
  componentPermission: false,
};

export default AuthGuard;
